import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

type ItemKind = 'material' | 'service';

/**
 * Identity of a ticket line item: its tickets_item id or — while the ticket is
 * still being created and its rows are only buffered client-side — the
 * job_scope_item_id it was picked from. The same product can sit on a ticket
 * twice, so the product/service id is never used.
 */
export function ticketItemKey(item: any): string {
  return item?.id ? `id:${item.id}` : `jsi:${item?.job_scope_item_id}`;
}

/** The fields a checklist item keeps for a linked line item — enough to draw its chip. */
export function toChecklistLineItem(item: any): any {
  return {
    id: item?.id ?? null,
    job_scope_item_id: item?.job_scope_item_id ?? null,
    kind: item?.kind,
    item_name: item?.item_name,
    vendor_name: item?.vendor_name,
    quantity: item?.quantity ?? null,
    price: item?.price ?? null,
  };
}

/** What the API needs to link line items (TicketRepository::addTicketChecklistItems). */
export function toChecklistItemRefs(items: any[]): any[] {
  return (items || []).map(item => ({
    id: item?.id || null,
    job_scope_item_id: item?.job_scope_item_id || null,
  }));
}

/**
 * A checklist row's linked items minus any that are no longer on the ticket
 * (removed from Materials & Services). Null when nothing needs dropping, or
 * when the ticket's item list is not known yet.
 */
export function pruneChecklistItems(linked: any[], ticketItems: any[]): any[] | null {
  if (!Array.isArray(ticketItems) || !Array.isArray(linked) || !linked.length) {
    return null;
  }
  const onTicket = new Set(ticketItems.map(ticketItemKey));
  const kept = linked.filter(item => onTicket.has(ticketItemKey(item)));
  return kept.length === linked.length ? null : kept;
}

export function checklistItemKindIcon(item: any): string {
  return item?.kind === 'material' ? 'inventory_2' : 'handyman';
}

/**
 * Link a checklist item to the ticket's Materials & Services line items.
 *
 * Lists only the rows already on the ticket (`data.items`), split into
 * Materials and Services like the Materials & Services picker, and edits the
 * checklist item's whole selection: rows already linked open ticked, and
 * unticking one unlinks it on Save. Nothing here creates or changes a line item.
 */
@Component({
  selector: 'app-ticket-checklist-items-popup',
  templateUrl: './ticket-checklist-items-popup.component.html',
  styleUrls: ['./ticket-checklist-items-popup.component.scss'],
  standalone: false
})
export class TicketChecklistItemsPopupComponent {
  readonly index: any;
  readonly kindIcon = checklistItemKindIcon;

  /** Every line item on the ticket. */
  private readonly allItems: any[];
  /** Keys linked when the popup opened — Save stays off until this changes. */
  private readonly initialKeys: Set<string>;

  activeTab: ItemKind = 'material';
  search = '';
  /** Current selection, keyed by ticketItemKey, in the order it was made. */
  selected = new Map<string, any>();

  constructor(
    public dialogRef: MatDialogRef<TicketChecklistItemsPopupComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
  ) {
    this.index = data?.index;
    this.allItems = (data?.items || []).filter((item: any) => item && (item.id || item.job_scope_item_id));

    // Links whose line item has left the ticket are not offered back.
    const byKey = new Map(this.allItems.map(item => [ticketItemKey(item), item]));
    (data?.selected || []).forEach((item: any) => {
      const key = ticketItemKey(item);
      if (byKey.has(key)) {
        this.selected.set(key, byKey.get(key));
      }
    });
    this.initialKeys = new Set(this.selected.keys());

    // Open on Services when the ticket has no materials at all.
    if (!this.countFor('material') && this.countFor('service')) {
      this.activeTab = 'service';
    }
  }

  get hasAnyItems(): boolean {
    return this.allItems.length > 0;
  }

  countFor(kind: ItemKind): number {
    return this.allItems.filter(item => item.kind === kind).length;
  }

  selectedCountFor(kind: ItemKind): number {
    let count = 0;
    this.selected.forEach(item => { if (item.kind === kind) count++; });
    return count;
  }

  /** The active tab's rows, narrowed by the search box. */
  get visibleItems(): any[] {
    const term = this.search.trim().toLowerCase();
    return this.allItems
      .filter(item => item.kind === this.activeTab)
      .filter(item => !term || [item.item_name, item.vendor_name, item.description, item.linked_scope_name]
        .some(value => (value || '').toString().toLowerCase().includes(term)));
  }

  get selectedList(): any[] {
    return Array.from(this.selected.values());
  }

  get hasChanges(): boolean {
    if (this.selected.size !== this.initialKeys.size) {
      return true;
    }
    return Array.from(this.selected.keys()).some(key => !this.initialKeys.has(key));
  }

  setTab(kind: ItemKind): void {
    this.activeTab = kind;
  }

  isSelected(item: any): boolean {
    return this.selected.has(ticketItemKey(item));
  }

  toggle(item: any): void {
    const key = ticketItemKey(item);
    if (this.selected.has(key)) {
      this.selected.delete(key);
    } else {
      this.selected.set(key, item);
    }
  }

  save(): void {
    this.dialogRef.close({ index: this.index, items: this.selectedList.map(toChecklistLineItem) });
  }

  close(): void {
    this.dialogRef.close();
  }
}
